import React from 'react';
import {
  X, MapPin, Mail, Phone, Linkedin, Languages, Briefcase, UserCircle,
  ArrowRight, Mic, Globe, Send,
} from 'lucide-react';
import { absoluteUrl } from '../lib/api';
import { modalityChipMeta } from '../pages/DirectoryPage';
import BioContent from './BioContent';

function derivePairs(active, passive, excluded) {
  const A = (active || []).filter(Boolean);
  const P = (passive || []).filter(Boolean);
  const ex = new Set(excluded || []);
  const out = [];
  for (let i = 0; i < A.length; i += 1) for (let j = 0; j < A.length; j += 1) if (i !== j) out.push([A[i], A[j]]);
  for (const p of P) for (const a of A) out.push([p, a]);
  const seen = new Set();
  return out.filter(([f, t]) => {
    if (!f || !t || f === t) return false;
    if (ex.has(`${f}→${t}`)) return false;
    const k = `${f}|${t}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * FullProfilePreview — renders the form data EXACTLY like the public
 * InterpreterProfilePage (wide, "final" look), for use inside a modal so the
 * registrant can see how their published profile will look.
 */
export default function FullProfilePreview({ form, onClose }) {
  const fullName = (form.full_name || '').trim()
    || [form.nombres, form.apellido_paterno, form.apellido_materno].filter(Boolean).join(' ').trim()
    || 'Tu nombre';
  const initials = fullName.split(' ').slice(0, 2).map((n) => n.charAt(0)).join('').toUpperCase() || '?';
  const photo = form.photo_url ? absoluteUrl(form.photo_url) : '';
  const specialties = Array.isArray(form.specialty)
    ? form.specialty
    : (form.specialty ? String(form.specialty).split(',').map((s) => s.trim()).filter(Boolean) : []);
  const modalities = (form.modalities || []).filter(Boolean);
  const active = (form.active_languages || []).filter(Boolean);
  const website = form.website ? (/^https?:\/\//i.test(form.website) ? form.website : `https://${form.website}`) : '';
  const pairs = derivePairs(form.active_languages, form.passive_languages, form.excluded_language_pairs);

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto" onClick={onClose} data-testid="full-preview-modal">
      <div className="relative w-full max-w-4xl my-0 sm:my-6" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="full-preview-close"
          className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-charcoal-700 shadow-md flex items-center justify-center transition-colors"
          aria-label="Cerrar"
        >
          <X size={18} />
        </button>

        <div className="bg-gray-50 sm:rounded-2xl overflow-hidden shadow-2xl">
          {/* Gradient hero */}
          <div className="bg-gradient-to-br from-cmic-800 via-cmic-700 to-cmic-600 pt-10 pb-20 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-6 left-10 w-56 h-56 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-16 w-72 h-72 bg-cmic-300 rounded-full blur-3xl" />
            </div>
            <p className="relative text-center text-cmic-100 text-xs font-semibold uppercase tracking-widest">Así se verá tu perfil público</p>
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 1440 60" fill="none" className="w-full">
                <path d="M0 60L60 55C120 50 240 40 360 35C480 30 600 30 720 32.5C840 35 960 40 1080 42.5C1200 45 1320 45 1380 45L1440 45V60H0Z" fill="#f9fafb" />
              </svg>
            </div>
          </div>

          <div className="px-4 sm:px-8 -mt-12 relative z-10 pb-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-7">
                  {photo ? (
                    <img src={photo} alt={fullName} className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-cover object-top shadow-lg flex-shrink-0" />
                  ) : (
                    <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl bg-gradient-to-br from-cmic-500 to-cmic-700 flex items-center justify-center shadow-lg flex-shrink-0">
                      <span className="text-white font-bold text-3xl sm:text-4xl">{initials}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl sm:text-3xl font-bold text-charcoal-800 mb-3">{fullName}</h1>
                    {form.city && (
                      <div className="inline-flex items-center gap-2 text-charcoal-500 text-sm bg-gray-50 px-3 py-1.5 rounded-lg mb-4">
                        <MapPin size={15} className="text-cmic-500" />{form.city}
                      </div>
                    )}
                    {specialties.length > 0 && (
                      <div className="mb-5">
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-cmic-600 mb-2 flex items-center gap-1.5"><Briefcase size={13} /> Especialidades</h3>
                        <div className="flex flex-wrap gap-2">
                          {specialties.map((s) => <span key={s} className="inline-flex items-center text-xs font-medium bg-cmic-50 text-cmic-700 px-3 py-1.5 rounded-full border border-cmic-100">{s}</span>)}
                        </div>
                      </div>
                    )}
                    {modalities.length > 0 && (
                      <div className="mb-5">
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-violet-700 mb-2 flex items-center gap-1.5"><Mic size={13} /> Modalidades de interpretación</h3>
                        <div className="flex flex-wrap gap-2">
                          {modalities.map((m) => {
                            const meta = modalityChipMeta(m); const I = meta.icon;
                            return <span key={m} className="inline-flex items-center gap-1.5 text-xs font-medium bg-violet-50 text-violet-700 px-3 py-1.5 rounded-full border border-violet-100"><I size={12} className="text-violet-500" />{meta.full}</span>;
                          })}
                        </div>
                      </div>
                    )}
                    {active.length > 0 && (
                      <div>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-cmic-600 mb-2 flex items-center gap-1.5"><Languages size={13} /> Idiomas activos</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {active.map((l) => <span key={l} className="inline-flex items-center text-xs font-medium bg-cmic-100 text-cmic-700 px-2.5 py-1 rounded-full">{l}</span>)}
                        </div>
                        <p className="text-[10px] text-charcoal-400 mt-1.5">Interpreta hacia estos idiomas</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="border-t border-gray-100 grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                <div className="lg:col-span-2 p-6 sm:p-8 space-y-8 min-w-0">
                  {pairs.length > 0 && (
                    <div>
                      <h2 className="text-lg font-bold text-charcoal-800 mb-4 flex items-center gap-2">
                        <Languages size={20} className="text-cmic-500" /> Combinación Lingüística
                        <span className="text-xs font-normal text-charcoal-400 ml-1">· {pairs.length} {pairs.length === 1 ? 'combinación' : 'combinaciones'}</span>
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {pairs.map(([f, t], idx) => (
                          <div key={`${f}-${t}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-br from-white via-cmic-50/40 to-white border border-cmic-100">
                            <div className="flex-1 min-w-0 text-right">
                              <span className="block text-[10px] uppercase tracking-wider text-cmic-500/80 font-semibold mb-0.5">Desde</span>
                              <span className="block font-semibold text-charcoal-800 truncate" title={f}>{f}</span>
                            </div>
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cmic-500 text-white flex items-center justify-center shadow-sm"><ArrowRight size={14} /></div>
                            <div className="flex-1 min-w-0">
                              <span className="block text-[10px] uppercase tracking-wider text-cmic-500/80 font-semibold mb-0.5">Hacia</span>
                              <span className="block font-semibold text-charcoal-800 truncate" title={t}>{t}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {form.bio && (
                    <div>
                      <h2 className="text-lg font-bold text-charcoal-800 mb-4 flex items-center gap-2"><UserCircle size={20} className="text-cmic-500" /> Semblanza</h2>
                      <BioContent bio={form.bio} className="text-charcoal-600 leading-relaxed text-[15px]" />
                    </div>
                  )}

                  {form.references_text && (() => {
                    const items = form.references_text.split('\n').map((s) => s.trim()).filter(Boolean);
                    if (items.length === 0) return null;
                    const twoCols = items.length > 4;
                    return (
                      <div>
                        <h2 className="text-lg font-bold text-charcoal-800 mb-4">Certificaciones</h2>
                        <ol className={`grid gap-3 ${twoCols ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
                          {items.map((ref, i) => (
                            <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100 min-w-0">
                              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cmic-100 text-cmic-700 text-sm font-bold flex items-center justify-center tabular-nums">{i + 1}</span>
                              <span className="text-charcoal-600 text-sm leading-relaxed break-words min-w-0">{ref}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    );
                  })()}
                </div>

                <div className="p-6 sm:p-8 bg-gray-50/50">
                  <h2 className="text-lg font-bold text-charcoal-800 mb-6">Información de Contacto</h2>
                  <div className="space-y-4">
                    {form.email && (
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0"><Mail size={18} className="text-cmic-600" /></div>
                        <div className="min-w-0"><p className="text-xs text-charcoal-400 mb-0.5">Email</p><p className="text-sm text-cmic-600 truncate">{form.email}</p></div>
                      </div>
                    )}
                    {(form.additional_emails || []).filter(Boolean).map((em) => (
                      <div key={em} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cmic-50 flex items-center justify-center flex-shrink-0"><Mail size={16} className="text-cmic-500" /></div>
                        <div className="min-w-0"><p className="text-xs text-charcoal-400 mb-0.5">Email adicional</p><p className="text-sm text-cmic-600 truncate">{em}</p></div>
                      </div>
                    ))}
                    {form.phone && (
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0"><Phone size={18} className="text-cmic-600" /></div>
                        <div><p className="text-xs text-charcoal-400 mb-0.5">Teléfono</p><p className="text-sm text-charcoal-700">{form.phone}</p></div>
                      </div>
                    )}
                    {(form.additional_phones || []).filter(Boolean).map((ph) => (
                      <div key={ph} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cmic-50 flex items-center justify-center flex-shrink-0"><Phone size={16} className="text-cmic-500" /></div>
                        <div><p className="text-xs text-charcoal-400 mb-0.5">Teléfono adicional</p><p className="text-sm text-charcoal-700">{ph}</p></div>
                      </div>
                    ))}
                    {form.city && (
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0"><MapPin size={18} className="text-cmic-600" /></div>
                        <div><p className="text-xs text-charcoal-400 mb-0.5">Ubicación</p><p className="text-sm text-charcoal-700">{form.city}</p></div>
                      </div>
                    )}
                    {form.linkedin_url && (
                      <div className="flex items-center gap-3 mt-6 bg-[#0077B5] text-white rounded-xl px-4 py-3 shadow-sm">
                        <Linkedin size={20} /><span className="text-sm font-medium">Ver perfil en LinkedIn</span>
                      </div>
                    )}
                    {website && (
                      <div className="flex items-center gap-3 mt-3 bg-charcoal-700 text-white rounded-xl px-4 py-3 shadow-sm">
                        <Globe size={20} /><span className="text-sm font-medium">Visitar página web</span>
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2 mt-3 bg-gradient-to-r from-cmic-600 to-cmic-700 text-white rounded-xl px-4 py-3 shadow-sm font-medium text-sm">
                      <Send size={16} /> Solicitar este intérprete
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
